import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const MAX_MODES = 64;
export const MAX_BANKS = 8;
export const MIN_FREQUENCY_HZ = 20;
export const MAX_FREQUENCY_HZ = 24_000;
export const MIN_DECAY_SECONDS = 0.005;
export const MAX_DECAY_SECONDS = 30;
export const MAX_MODE_GAIN = 2;
export const MIN_ENERGY_LIMIT = 0.001;
export const MAX_ENERGY_LIMIT = 4;

export interface ModalModeDefinition {
  readonly frequencyHz: number;
  readonly decaySeconds: number;
  readonly gain: number;
  readonly pan: number;
  readonly excitation: number;
}

export interface ModalBankDefinition {
  readonly id: string;
  readonly modes: readonly ModalModeDefinition[];
}

export interface ModalResonatorOptions {
  readonly sampleRate: number;
  readonly banks: readonly ModalBankDefinition[];
  readonly bank?: number;
  readonly frequencyScale?: number;
  readonly decayScale?: number;
  readonly outputGain?: number;
  readonly excitation?: number;
  /** Maximum squared state magnitude of one mode. */
  readonly energyLimit?: number;
}

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const assertIntegerRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be an integer in [${minimum}, ${maximum}]`);
  }
};

const DEFAULT_BANKS: readonly ModalBankDefinition[] = Object.freeze([
  Object.freeze({
    id: 'default',
    modes: Object.freeze([
      Object.freeze({ frequencyHz: 220, decaySeconds: 0.8, gain: 0.7, pan: -0.35, excitation: 1 }),
      Object.freeze({ frequencyHz: 554.37, decaySeconds: 0.45, gain: 0.45, pan: 0.3, excitation: 0.72 }),
      Object.freeze({ frequencyHz: 932.33, decaySeconds: 0.28, gain: 0.28, pan: 0.65, excitation: 0.5 }),
    ]),
  }),
]);

/**
 * A banked, host-independent modal resonator. All storage is allocated by the
 * constructor; processInto writes exclusively to caller-owned buffers.
 */
export class ModalResonator {
  readonly sampleRate: number;
  readonly bankCount: number;
  readonly modeCount: number;

  private bank: number;
  private frequencyScale: number;
  private decayScale: number;
  private outputGain: number;
  private excitationAmount: number;
  private energyLimit: number;

  private readonly bankStarts = new Uint8Array(MAX_BANKS);
  private readonly bankCounts = new Uint8Array(MAX_BANKS);
  private readonly frequencies = new Float64Array(MAX_MODES);
  private readonly decays = new Float64Array(MAX_MODES);
  private readonly gains = new Float64Array(MAX_MODES);
  private readonly excitations = new Float64Array(MAX_MODES);
  private readonly panLeft = new Float64Array(MAX_MODES);
  private readonly panRight = new Float64Array(MAX_MODES);
  private readonly cosine = new Float64Array(MAX_MODES);
  private readonly sine = new Float64Array(MAX_MODES);
  private readonly damping = new Float64Array(MAX_MODES);
  private readonly real = new Float64Array(MAX_MODES);
  private readonly imaginary = new Float64Array(MAX_MODES);

  constructor(options: ModalResonatorOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    if (options.banks.length < 1 || options.banks.length > MAX_BANKS) {
      throw new RangeError(`bank count must be in [1, ${MAX_BANKS}]`);
    }
    this.sampleRate = options.sampleRate;
    this.bankCount = options.banks.length;
    this.bank = options.bank ?? 0;
    this.frequencyScale = options.frequencyScale ?? 1;
    this.decayScale = options.decayScale ?? 1;
    this.outputGain = options.outputGain ?? 1;
    this.excitationAmount = options.excitation ?? 1;
    this.energyLimit = options.energyLimit ?? 1;

    assertIntegerRange('bank', this.bank, 0, this.bankCount - 1);
    assertRange('frequencyScale', this.frequencyScale, 0.25, 4);
    assertRange('decayScale', this.decayScale, 0.1, 4);
    assertRange('outputGain', this.outputGain, 0, 2);
    assertRange('excitation', this.excitationAmount, 0, 1);
    assertRange('energyLimit', this.energyLimit, MIN_ENERGY_LIMIT, MAX_ENERGY_LIMIT);

    const ids = new Set<string>();
    let modeIndex = 0;
    for (let bankIndex = 0; bankIndex < options.banks.length; bankIndex += 1) {
      const bank = options.banks[bankIndex]!;
      if (!/^[a-z][a-z0-9-]*$/.test(bank.id) || ids.has(bank.id)) throw new RangeError(`bank id is invalid or duplicated: ${bank.id}`);
      ids.add(bank.id);
      if (bank.modes.length < 1) throw new RangeError(`bank ${bank.id} must contain at least one mode`);
      if (modeIndex + bank.modes.length > MAX_MODES) throw new RangeError(`total mode count exceeds ${MAX_MODES}`);
      this.bankStarts[bankIndex] = modeIndex;
      this.bankCounts[bankIndex] = bank.modes.length;
      for (const mode of bank.modes) {
        this.validateMode(mode);
        this.frequencies[modeIndex] = mode.frequencyHz;
        this.decays[modeIndex] = mode.decaySeconds;
        this.gains[modeIndex] = mode.gain;
        this.excitations[modeIndex] = mode.excitation;
        const angle = (mode.pan + 1) * Math.PI * 0.25;
        this.panLeft[modeIndex] = Math.cos(angle);
        this.panRight[modeIndex] = Math.sin(angle);
        modeIndex += 1;
      }
    }
    this.modeCount = modeIndex;
    this.updateCoefficients();
  }

  reset(): void {
    this.real.fill(0);
    this.imaginary.fill(0);
  }

  setParameter(id: string, value: number): void {
    if (id === 'bank') {
      assertIntegerRange(id, value, 0, this.bankCount - 1);
      this.bank = value;
      return;
    }
    if (id === 'frequency-scale') {
      assertRange(id, value, 0.25, 4);
      this.frequencyScale = value;
      this.updateCoefficients();
      return;
    }
    if (id === 'decay-scale') {
      assertRange(id, value, 0.1, 4);
      this.decayScale = value;
      this.updateCoefficients();
      return;
    }
    if (id === 'output-gain') {
      assertRange(id, value, 0, 2);
      this.outputGain = value;
      return;
    }
    if (id === 'excitation') {
      assertRange(id, value, 0, 1);
      this.excitationAmount = value;
      return;
    }
    if (id === 'energy-limit') {
      assertRange(id, value, MIN_ENERGY_LIMIT, MAX_ENERGY_LIMIT);
      this.energyLimit = value;
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(
    input: Float32Array,
    left: Float32Array,
    right: Float32Array,
    energy?: Float32Array,
    frames = left.length,
  ): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || left.length < frames || right.length < frames || (energy !== undefined && energy.length < frames)) {
      throw new RangeError('buffers are too short for frames');
    }

    // Validate the complete block before touching caller-owned outputs or
    // resonator state. A late-invalid sample must fail transactionally.
    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(input[frame]!)) throw new RangeError('excitation samples must be finite');
      if (energy !== undefined) {
        const energyAmount = energy[frame]!;
        if (!Number.isFinite(energyAmount) || energyAmount < 0 || energyAmount > 1) throw new RangeError('energy samples must be in [0, 1]');
      }
    }

    const start = this.bankStarts[this.bank]!;
    const end = start + this.bankCounts[this.bank]!;
    const excitationAmount = this.excitationAmount;
    const outputGain = this.outputGain;
    const energyLimit = this.energyLimit;

    for (let frame = 0; frame < frames; frame += 1) {
      const sample = input[frame]!;
      const energyAmount = energy === undefined ? 1 : energy[frame]!;
      let leftSample = 0;
      let rightSample = 0;

      for (let mode = start; mode < end; mode += 1) {
        const previousReal = this.real[mode]!;
        const previousImaginary = this.imaginary[mode]!;
        const damping = this.damping[mode]!;
        let nextReal = damping * (this.cosine[mode]! * previousReal - this.sine[mode]! * previousImaginary)
          + sample * energyAmount * excitationAmount * this.excitations[mode]!;
        let nextImaginary = damping * (this.sine[mode]! * previousReal + this.cosine[mode]! * previousImaginary);
        const stateEnergy = nextReal * nextReal + nextImaginary * nextImaginary;
        if (stateEnergy > energyLimit) {
          const normalization = Math.sqrt(energyLimit / stateEnergy);
          nextReal *= normalization;
          nextImaginary *= normalization;
        }
        if (!Number.isFinite(nextReal) || !Number.isFinite(nextImaginary)) {
          nextReal = 0;
          nextImaginary = 0;
        }
        this.real[mode] = Math.abs(nextReal) < 1e-30 ? 0 : nextReal;
        this.imaginary[mode] = Math.abs(nextImaginary) < 1e-30 ? 0 : nextImaginary;
        const modalOutput = nextReal * this.gains[mode]!;
        leftSample += modalOutput * this.panLeft[mode]!;
        rightSample += modalOutput * this.panRight[mode]!;
      }
      left[frame] = Math.max(-1, Math.min(1, leftSample * outputGain));
      right[frame] = Math.max(-1, Math.min(1, rightSample * outputGain));
    }
  }

  private validateMode(mode: ModalModeDefinition): void {
    const maximum = Math.min(MAX_FREQUENCY_HZ, this.sampleRate * 0.49);
    assertRange('mode frequencyHz', mode.frequencyHz, MIN_FREQUENCY_HZ, maximum);
    assertRange('mode decaySeconds', mode.decaySeconds, MIN_DECAY_SECONDS, MAX_DECAY_SECONDS);
    assertRange('mode gain', mode.gain, 0, MAX_MODE_GAIN);
    assertRange('mode pan', mode.pan, -1, 1);
    assertRange('mode excitation', mode.excitation, 0, 1);
  }

  private updateCoefficients(): void {
    const maximumFrequency = Math.min(MAX_FREQUENCY_HZ, this.sampleRate * 0.49);
    for (let mode = 0; mode < this.modeCount; mode += 1) {
      const frequency = Math.max(MIN_FREQUENCY_HZ, Math.min(maximumFrequency, this.frequencies[mode]! * this.frequencyScale));
      const decay = Math.max(MIN_DECAY_SECONDS, Math.min(MAX_DECAY_SECONDS, this.decays[mode]! * this.decayScale));
      const phase = 2 * Math.PI * frequency / this.sampleRate;
      this.cosine[mode] = Math.cos(phase);
      this.sine[mode] = Math.sin(phase);
      this.damping[mode] = Math.exp(-1 / (decay * this.sampleRate));
    }
  }
}

class ModalResonatorModuleInstance implements DspModuleInstance {
  private readonly resonator: ModalResonator;

  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.resonator = new ModalResonator({ sampleRate, banks: DEFAULT_BANKS });
  }

  reset(): void { this.resonator.reset(); }
  setParameter(id: string, value: number): void { this.resonator.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const excitation = block.inputs.excitation;
    const left = block.outputs.left;
    const right = block.outputs.right;
    if (!excitation || excitation.length < block.frames) throw new RangeError('excitation buffer is missing or too short');
    if (!left || left.length < block.frames || !right || right.length < block.frames) throw new RangeError('output buffer is missing or too short');
    const energy = block.controls.energy;
    if (energy && energy.length < block.frames) throw new RangeError('energy buffer is too short');
    this.resonator.processInto(excitation, left, right, energy, block.frames);
  }
}

export const mod008 = defineDspModule({
  descriptor: {
    id: 'mir.mod-008',
    name: 'Banked modal resonator',
    version: '0.1.0',
    inputs: [
      { id: 'excitation', kind: 'audio', channels: 1 },
      { id: 'energy', kind: 'control', channels: 1 },
    ],
    outputs: [
      { id: 'left', kind: 'audio', channels: 1 },
      { id: 'right', kind: 'audio', channels: 1 },
    ],
    parameters: [
      { id: 'bank', defaultValue: 0, minValue: 0, maxValue: MAX_BANKS - 1, unit: 'index' },
      { id: 'frequency-scale', defaultValue: 1, minValue: 0.25, maxValue: 4, unit: 'ratio' },
      { id: 'decay-scale', defaultValue: 1, minValue: 0.1, maxValue: 4, unit: 'ratio' },
      { id: 'output-gain', defaultValue: 1, minValue: 0, maxValue: 2, unit: 'linear' },
      { id: 'excitation', defaultValue: 1, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'energy-limit', defaultValue: 1, minValue: MIN_ENERGY_LIMIT, maxValue: MAX_ENERGY_LIMIT, unit: 'power' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid DSP instance context');
    return new ModalResonatorModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
